import type { RouterConfig } from '@nuxt/schema'
import { getAnchorElement } from '@gro-up/ui/utils'
import { useNuxtApp } from '#app'
import { START_LOCATION } from 'vue-router'

/**
 * Resolve the scroll target for a navigation, assuming the destination is already rendered.
 */
const resolvePosition = (
  to: Parameters<NonNullable<RouterConfig['scrollBehavior']>>[0],
  from: Parameters<NonNullable<RouterConfig['scrollBehavior']>>[1],
  savedPosition: Parameters<NonNullable<RouterConfig['scrollBehavior']>>[2],
) => {
  const isSamePage = to.path === from.path

  // A hash always wins. Editors can link to a section on the page the visitor is already on,
  // which the same-page branch below would otherwise skip.
  if (to.hash) {
    // Resolved by hand instead of handing vue-router the selector: CMS anchors are free text and
    // may contain spaces or start with a digit, which makes `querySelector` throw.
    const element = getAnchorElement(to.hash)

    if (element) {
      return {
        el: element,
        top: getAnchorOffset(element),
        behavior: isSamePage ? ('smooth' as const) : ('auto' as const),
      }
    }

    return isSamePage ? false : { top: 0 }
  }

  // Query-only navigation (filtering/paginating on the same page): preserve scroll
  // position so the page does not jump to the top on every filter change.
  if (isSamePage) {
    return false
  }

  return savedPosition ?? { top: 0 }
}

/**
 * Room to leave above an anchor, so a sticky header does not cover it. Mirrors what Nuxt's own
 * scrollBehavior does, which lets the offset be tuned from CSS instead of from here.
 */
const getAnchorOffset = (element: HTMLElement) => {
  const scrollMarginTop = Number.parseFloat(getComputedStyle(element).scrollMarginTop) || 0
  const scrollPaddingTop = Number.parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0

  return scrollMarginTop + scrollPaddingTop
}

export default <RouterConfig>{
  scrollBehavior(to, from, savedPosition) {
    // Same page or first render: the DOM the target lives in is already there.
    if (from === START_LOCATION || to.path === from.path) {
      return resolvePosition(to, from, savedPosition)
    }

    // Page blocks are async components (see `createComponentMapper`), so right after a route change
    // the anchor either does not exist yet or sits at a position that still shifts. `page:loading:end`
    // fires once the page has settled, which is the signal Nuxt's own scrollBehavior waits for too.
    return new Promise(resolve => {
      useNuxtApp().hooks.hookOnce('page:loading:end', () => {
        requestAnimationFrame(() => resolve(resolvePosition(to, from, savedPosition)))
      })
    })
  },
}
